// Runs the full bot-gameplay simulation (SimulationRunner + GameBot) over a batch
// of levels handed over by the level editor, and posts per-level shot statistics
// back to the editor window. Companion of stability_check.ts, which only settles
// the tower; this one actually plays the levels N times each.

import { SimulationRunner, SimulationStats } from "../simulation/simulation_runner.js";
import { yieldToBrowser } from "../simulation/yield_to_browser.js";
import { awaitBatch } from "./stability_check.js";
import { TestLevelBridge } from "./test_level_bridge.js";

export interface SimulationBatchEntry
{
  level: number;
  runs: number;
  wins: number;
  timeouts: number;
  avg_shots: number;
  median_shots: number;
  min_shots: number;
  max_shots: number;
  p50: number;
  p75: number;
  p90: number;
  real_time_seconds: number;
  histogram: Record<number, number>;
  pass_rate: Record<number, number>;
}

function toEntry( stats: SimulationStats ): SimulationBatchEntry
{
  return {
    level: stats.level,
    runs: stats.runs,
    wins: stats.wins,
    timeouts: stats.timeouts,
    avg_shots: Number( stats.avg_shots.toFixed( 2 ) ),
    median_shots: stats.median_shots,
    min_shots: stats.min_shots,
    max_shots: stats.max_shots,
    p50: stats.p50,
    p75: stats.p75,
    p90: stats.p90,
    real_time_seconds: Number( stats.real_time_seconds.toFixed( 1 ) ),
    histogram: stats.histogram,
    pass_rate: stats.pass_rate,
  };
}

let paused = false;
let pause_listener_installed = false;

function installPauseListener(): void
{
  if ( pause_listener_installed )
    return;

  pause_listener_installed = true;

  window.addEventListener( "message", ( event: MessageEvent ) =>
  {
    const type = ( event.data as { type?: string } | null )?.type;

    if ( type === "brickbreaker-sim-pause" )
      paused = true;

    if ( type === "brickbreaker-sim-resume" )
      paused = false;
  } );
}

function sleep( ms: number ): Promise<void>
{
  return new Promise( resolve => setTimeout( resolve, ms ) );
}

let error_reporter_installed = false;

function reportError( message: string ): void
{
  try
  {
    const shard = TestLevelBridge.getSimShard();
    getChannel()?.postMessage( { type: "sim-shard-error", shard, message } );
    window.opener?.postMessage( { type: "brickbreaker-sim-error", shard, message }, "*" );
  }
  catch
  {
    // editor tab may be gone — the error still lands in this window's console
  }
}

function installErrorReporter(): void
{
  if ( error_reporter_installed )
    return;

  error_reporter_installed = true;

  window.addEventListener( "error", ( event ) =>
    reportError( `${event.message} @ ${event.filename?.split( "/" ).pop() ?? "?"}:${event.lineno}` )
  );

  window.addEventListener( "unhandledrejection", ( event ) =>
    reportError( `unhandled rejection: ${String( event.reason ).slice( 0, 200 )}` )
  );
}

async function pauseGate( on_tick: () => void ): Promise<void>
{
  while ( paused )
  {
    on_tick();
    await sleep( 500 );
  }
}

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null
{
  const id = TestLevelBridge.getSimChannel();
  if ( !id )
    return null;

  channel ??= new BroadcastChannel( id );
  return channel;
}

function reportProgress( level: number, index: number, total: number, done: number, runs: number, run_sim_seconds?: number ): void
{
  const run_line = run_sim_seconds !== undefined
    ? `\nпоточному прогону ${Math.round( run_sim_seconds )}с сим-часу`
    : "";

  TestLevelBridge.showBatchText(
    `Симуляція боєм\nрівень ${level} (${index} з ${total})\nпрогін ${done} з ${runs}${run_line}`
  );

  const shard = TestLevelBridge.getSimShard();

  try
  {
    getChannel()?.postMessage( { type: "sim-shard-progress", shard, level, index, total, done, runs } );

    window.opener?.postMessage(
      { type: "brickbreaker-simulation-progress", shard, level, index, total, done, runs },
      "*"
    );
  }
  catch
  {
    // editor tab may be gone — the run itself still finishes and logs to console
  }
}

/**
 * Runs one chunk of work handed over by the editor and returns its results.
 * A chunk is usually a single level: the editor keeps a shared queue and only
 * hands out the next piece of work when a window asks for it, so whichever
 * window is free picks up the next level instead of every window being given a
 * fixed slice up front and the fast ones idling while the slow ones grind.
 */
async function runChunk(): Promise<SimulationBatchEntry[]>
{
  const levels = TestLevelBridge.getBatchLevels();
  const runs = TestLevelBridge.getSimRuns();
  const results: SimulationBatchEntry[] = [];

  if ( levels.length === 0 )
    reportError( "пачка розпарсилась у 0 рівнів — клієнт не знає цих блоків? (стара збірка?)" );

  for ( const level_number of levels )
  {
    const index = results.length + 1;
    reportProgress( level_number, index, levels.length, 0, runs );

    // Страховочний пульс: під час init рівня ігровий цикл ще не крутиться і
    // звичайні хартбіти мовчать — а редактор рахує тишу.
    let last_done = 0;
    const keepalive = window.setInterval(
      () => reportProgress( level_number, index, levels.length, last_done, runs ),
      5000
    );

    try
    {
      const stats = await new SimulationRunner().run(
        level_number,
        runs,
        false,
        ( done, _total, run_sim_seconds ) =>
        {
          last_done = done;
          reportProgress( level_number, index, levels.length, done, runs, run_sim_seconds );
        },
        async ( done ) =>
        {
          if ( !paused )
            return;

          await pauseGate( () =>
          {
            reportProgress( level_number, index, levels.length, done, runs );
            TestLevelBridge.showBatchText( `Пауза\nрівень ${level_number}\nпрогін ${done} з ${runs}` );
          } );
        }
      );

      results.push( toEntry( stats ) );
    }
    catch ( e )
    {
      console.warn( `[SimBatch] level ${level_number} failed to simulate`, e );
      reportError( `рівень ${level_number} не досимулювався: ${String( e ).slice( 0, 200 )}` );
      results.push( {
        level: level_number,
        runs,
        wins: 0,
        timeouts: runs,
        avg_shots: 0,
        median_shots: 0,
        min_shots: 0,
        max_shots: 0,
        p50: 0,
        p75: 0,
        p90: 0,
        real_time_seconds: 0,
        histogram: {},
        pass_rate: {},
      } );
    }
    finally
    {
      window.clearInterval( keepalive );
    }

    await yieldToBrowser();
  }

  return results;
}

function report( results: SimulationBatchEntry[] ): void
{
  console.log( "[SimBatch] Результати:", results );

  try
  {
    getChannel()?.postMessage( {
      type: "sim-shard-results",
      shard: TestLevelBridge.getSimShard(),
      results,
    } );

    window.opener?.postMessage( { type: "brickbreaker-simulation", results }, "*" );
  }
  catch ( e )
  {
    console.warn( "[SimBatch] could not report back to the editor", e );
  }
}

export async function runSimulationBatch(): Promise<void>
{
  installPauseListener();
  installErrorReporter();

  // Say something BEFORE the first wait. Until the editor sends work there is
  // nothing on screen but the game's own background, which is indistinguishable
  // from a window that never finished loading.
  TestLevelBridge.showBatchText( "Симуляція боєм\nчекаю пачку від редактора…" );

  let batch = await awaitBatch();
  let done = 0;

  while ( batch.count > 0 )
  {
    const results = await runChunk();
    done += results.length;
    report( results );

    TestLevelBridge.showBatchText( `Готово рівнів: ${done}\nчекаю наступний від редактора…` );

    // Ask for more. Clearing first matters: awaitBatch short-circuits when a
    // batch is already loaded, and an editor that has nothing left answers with
    // an empty list, which ends the loop. An editor too old to answer at all
    // simply times out — one idle wait, then the window closes as before.
    TestLevelBridge.setBatch( [] );

    const idle_tick = () =>
    {
      try
      {
        getChannel()?.postMessage( { type: "sim-shard-progress", shard: TestLevelBridge.getSimShard() } );
        window.opener?.postMessage( { type: "brickbreaker-simulation-progress", shard: TestLevelBridge.getSimShard() }, "*" );
      }
      catch
      {
        // editor tab may be gone — resume will still arrive if it exists
      }

      TestLevelBridge.showBatchText( "Пауза\nчекаю продовження…" );
    };

    if ( paused )
      await pauseGate( idle_tick );

    batch = await awaitBatch();

    while ( batch.count === 0 && batch.timedOut && paused )
    {
      await pauseGate( idle_tick );
      batch = await awaitBatch();
    }
  }

  if ( batch.timedOut && done === 0 )
  {
    // The editor never said a word. That is the one case worth keeping the
    // window around for — closing would hide the only clue the user has.
    TestLevelBridge.showBatchText(
      "Редактор так і не надіслав жодного рівня.\n" +
      "Перевір адресу клієнта в редакторі й дозвіл на спливаючі вікна.\n" +
      "Це вікно можна закрити."
    );
    return;
  }

  TestLevelBridge.showBatchText(
    done > 0
      ? `Симуляція завершена: ${done} рівн.\nРезультат надіслано в редактор — вікно закриється саме`
      : "Уся робота вже розібрана іншими вікнами.\nВікно закриється саме"
  );

  // Script-opened windows may close themselves once done (noopener ones included).
  setTimeout( () => window.close(), 1500 );
}

/**
 * The one window that still has an opener: relays BroadcastChannel traffic from
 * the noopener shard windows (which run in their own processes and cannot talk
 * to the editor directly) back to the editor via postMessage.
 */
export function runSimulationCollector(): void
{
  const id = TestLevelBridge.getSimChannel();
  if ( !id )
    return;

  const bus = new BroadcastChannel( id );
  let shards_done = 0;

  TestLevelBridge.showBatchText( "Збираю результати з віконець…" );

  bus.onmessage = ( event: MessageEvent ) =>
  {
    const message = event.data as { type?: string; shard?: number } | null;
    if ( !message )
      return;

    try
    {
      if ( message.type === "sim-shard-progress" )
        window.opener?.postMessage( { ...message, type: "brickbreaker-simulation-progress" }, "*" );

      if ( message.type === "sim-shard-error" )
        window.opener?.postMessage( { ...message, type: "brickbreaker-sim-error" }, "*" );

      if ( message.type === "sim-shard-results" )
      {
        shards_done += 1;
        TestLevelBridge.showBatchText( `Збираю результати з віконець…\nготово шардів: ${shards_done}` );
        window.opener?.postMessage( { ...message, type: "brickbreaker-simulation-shard" }, "*" );
      }
    }
    catch ( e )
    {
      console.warn( "[SimCollector] relay failed", e );
    }
  };

  try
  {
    window.opener?.postMessage( { type: "brickbreaker-sim-collector-ready" }, "*" );
  }
  catch ( e )
  {
    console.warn( "[SimCollector] could not reach the editor", e );
  }
}
